import { Injectable, inject } from '@angular/core';
import { hasKeys, isNil, Nillable } from '@web/utils';
import { HttpParams } from '../enums/http-params';
import { QueryParamsTransformerService } from '../../json/services/query-params-transformer.service';
import { RequiredMethodCallException } from '../../exceptions/required-method-call.exception';

@Injectable({
  providedIn: 'root',
})
export class ApiUrlBuilder {
  /* prettier-ignore */
  private readonly queryParamsTransformer = inject(QueryParamsTransformerService);

  private _host: Nillable<string>;
  private _path: Nillable<string>;
  private _params: Nillable<{ [key in HttpParams]?: string }>;
  private _queryParams: Nillable<object>;

  public constructor() {
    this.reset();
  }

  public reset(): this {
    this._path = null;
    this._params = null;
    this._queryParams = null;

    return this;
  }

  public host(host: string): this {
    this._host = host;

    return this;
  }

  public path(path: string): this {
    this._path = path;

    return this;
  }

  public params(params: { [key in HttpParams]?: string }): this {
    this._params = params;

    return this;
  }

  public queryParams(queryParams: object): this {
    this._queryParams = queryParams;

    return this;
  }

  public build(): string {
    this.requireHost();
    this.requirePath();

    let url = this.url();

    if (this.hasValidParams(this._params)) {
      url = this.appendParams(url, this._params);
    }

    if (this.hasValidQueryParams(this._queryParams)) {
      url = this.appendQueryParams(url, this._queryParams);
    }

    this.reset();

    return url;
  }

  private requireHost(): void {
    if (!isNil(this._host)) {
      return;
    }

    throw new RequiredMethodCallException(this.host.name);
  }

  private requirePath(): void {
    if (!isNil(this._path)) {
      return;
    }

    throw new RequiredMethodCallException(this.path.name);
  }

  private url(): string {
    return `${this._host}/${this._path}`;
  }

  private appendParams(
    path: string,
    params: {
      [key in HttpParams]: string;
    },
  ): string {
    return Object.entries(params).reduce(
      (url, [key, value]) => url.replace(key, value),
      `${path}`,
    );
  }

  private appendQueryParams(path: string, queryParams: object): string {
    return `${path}?${this.queryParamsTransformer.stringify(queryParams)}`;
  }

  private hasValidParams(
    params: Nillable<{ [key in HttpParams]?: string }>,
  ): params is { [key in HttpParams]: string } {
    return !isNil(params) && hasKeys(params);
  }

  private hasValidQueryParams(
    queryParams: Nillable<object>,
  ): queryParams is object {
    return !isNil(queryParams) && hasKeys(queryParams);
  }
}
